import { useEffect, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import { X, CreditCard, Loader2, GraduationCap } from 'lucide-react';
import api from '../../services/api';

let stripePromise = null;
function getStripePromise() {
    if (!stripePromise) {
        stripePromise = api.get('/stripe/config.php').then(({ data }) => loadStripe(data.data.public_key));
    }
    return stripePromise;
}

const DUREE = { '1m': '1 mois', '6m': '6 mois', '12m': '12 mois' };

/**
 * Modal d'achat d'un pack de sièges formateur (Stripe embedded checkout).
 * À la confirmation, le webhook (formateur_seat_pack) génère le code à distribuer.
 */
export default function FormateurPackCheckoutModal({ pack, onClose, onSuccess }) {
    const [clientSecret, setClientSecret] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [simulating, setSimulating] = useState(false);
    const isDev = import.meta.env.DEV || new URLSearchParams(window.location.search).has('simulate');

    useEffect(() => { document.body.style.overflow = 'hidden'; return () => { document.body.style.overflow = ''; }; }, []);
    useEffect(() => {
        const h = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', h);
        return () => document.removeEventListener('keydown', h);
    }, [onClose]);

    const buy = async () => {
        setError(null);
        setLoading(true);
        try {
            const { data } = await api.post('/formateur/buy.php', { pack_id: pack.id });
            if (data.data?.client_secret) setClientSecret(data.data.client_secret);
        } catch (e) {
            setError(e.response?.data?.message || 'Erreur');
        }
        setLoading(false);
    };

    const simulate = async () => {
        setSimulating(true);
        setError(null);
        try {
            const { data } = await api.post('/formateur/buy.php', { pack_id: pack.id, simulate: true });
            if (data.data?.simulated) { onSuccess?.(); setTimeout(onClose, 1200); }
        } catch (e) {
            setError(e.response?.data?.message || 'Erreur');
            setSimulating(false);
        }
    };

    const price = Number(pack.price_eur).toFixed(2);

    return (
        <div className="checkout-modal-overlay" onClick={onClose}>
            <div className="checkout-modal" onClick={e => e.stopPropagation()}>
                <div className="checkout-modal__header">
                    <span className="checkout-modal__title"><GraduationCap size={18} /> {pack.nb_seats} sièges — {pack.scope_label}</span>
                    <button className="checkout-modal__close" onClick={onClose}><X size={20} /></button>
                </div>
                <div className="checkout-modal__body">
                    {error && <div className="alert alert-error" style={{ margin: 16 }}>{error}</div>}

                    {simulating && (
                        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                            <div className="spinner" style={{ margin: '0 auto 12px' }} />
                            <p><strong>Paiement simul&eacute;</strong> — code g&eacute;n&eacute;r&eacute; !</p>
                        </div>
                    )}

                    {!clientSecret && !simulating && (
                        <div className="sub-modal-plans" style={{ textAlign: 'center', padding: '8px 16px 16px' }}>
                            <div style={{ fontSize: '2.2em', fontWeight: 800, color: 'var(--primary)' }}>{price}&euro;</div>
                            <p style={{ color: 'var(--text-muted)', margin: '4px 0 16px' }}>
                                {pack.nb_seats} &eacute;l&egrave;ves &middot; {pack.scope_label} &middot; {DUREE[pack.duree] || pack.duree}
                            </p>
                            <button className="btn btn-primary sub-modal-cta" onClick={buy} disabled={loading}>
                                {loading ? <><Loader2 size={16} className="spin" /> Chargement...</> : <><CreditCard size={16} /> Acheter ({price}&euro;)</>}
                            </button>
                            {isDev && (
                                <button className="btn btn-sm"
                                    style={{ display: 'block', margin: '8px auto 0', fontSize: '0.75em', background: '#f59e0b', color: '#fff', border: 'none', borderRadius: 6, padding: '4px 12px', cursor: 'pointer' }}
                                    onClick={simulate}>
                                    Simuler (dev)
                                </button>
                            )}
                            <p className="sub-modal-note">
                                Paiement unique. Tu re&ccedil;ois <strong>1 code</strong> (valable pour {pack.nb_seats} &eacute;l&egrave;ves) &agrave; distribuer.
                                Tes &eacute;l&egrave;ves cr&eacute;ent un compte gratuit puis activent le code (aucun abonnement &agrave; payer).
                            </p>
                        </div>
                    )}

                    {clientSecret && !simulating && (
                        <EmbeddedCheckoutProvider stripe={getStripePromise()} options={{ clientSecret }}>
                            <EmbeddedCheckout />
                        </EmbeddedCheckoutProvider>
                    )}
                </div>
            </div>
        </div>
    );
}
